import { createColumnHelper } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import type { Item } from '@/models';
import { enhanceFetchedAssets } from '@/pages/assets/asset-listing/enhanceFetchedAsset';
import { AssetFilterType, GetAssetFilterTypeOptions } from '@/store/forms/Asset-Editor/AssetFilterTypeOptions';
import { initialPagination } from '@/store/forms/Channel/DynamicLodContentForm';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import useAppUtils from '@/utils/appUtils';
import { dateUtils } from '@/utils/dateUtils';

import { AssetDuration } from '../asset-duration';
import FuelActionbar from '../fuel-actionbar';
import FuelTable from '../fuel-table/FuelTable';
import FuelAssetSearch from '../FuelAssetSearch/FuelAssetSearch';
import FuelAssetThumbnail from '../FuelAssetThumbnail/FuelAssetThumbnail';
import FuelAssetTitle from '../FuelAssetTitle/FuelAssetTitle';

const ContentClipsTab = () => {
  const { ouData } = useSelector(applicationDataSelector);
  const [pagination, setPagination] = useState(initialPagination);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const [items, setItems] = useState<Item[]>([]);
  const flags = useFlags();
  const { alert } = useAppUtils();

  const { watch, getValues, setValue } = useFormContext();
  const clipGroupContentClips = watch('clipGroupContentClips');

  useEffect(() => {
    init();
  }, [clipGroupContentClips]);

  useEffect(() => {
    init();
    return () => {
      // reset();
      // console.log('cleaned up  container');
    };
  }, []);
  const init = async () => {
    const clips: Item[] = getValues('clipGroupContentClips');
    if (clips && clips.length > 0) {
      const myPagination = _.cloneDeep(initialPagination);
      myPagination.totalItems = clips.length;
      setPagination(myPagination);
      setItems(clips);
    }
  };

  const Thumbnail = useCallback(
    (cell) => (
      <FuelAssetThumbnail
        item={cell.row.original}
        permissions={permissions}
        // onClick={(event) => openEditPopup(event, cell.row.original)}
      />
    ),
    [permissions]
  );

  const columnHelper = createColumnHelper<any>();
  const columns = [
    columnHelper.accessor('handle', {
      header: () => '',
      width: '25px',
      sortable: false,
      cell: () => (
        <i aria-hidden="true" className="icon material-icons">
          reorder
        </i>
      ),
    }),
    columnHelper.accessor('thumbnailSrc', {
      header: '',
      cell: Thumbnail,
    }),
    columnHelper.accessor('title', {
      align: 'left',
      header: () => 'Title',
      width: 250,
      sortable: false,
      footer: (props) => props.column.id,
      cell: (cell) => {
        return (
          <FuelAssetTitle
            item={cell.row.original}
            permissions={permissions}
            // onClick={(event) => openEditPopup(event, cell.row.original)}
          />
        );
      },
    }),
    columnHelper.accessor('publicationDate', {
      header: `Publication Date (${ouData.timezoneAbbreviation})`,
      footer: (props) => props.column.id,
      width: 150,
      sortable: false,
      cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
    }),
    columnHelper.accessor('categoryString', {
      header: 'Categories',
      footer: (props) => props.column.id,
      width: 130,
      sortable: false,
      cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
    }),
    columnHelper.accessor('tagString', {
      header: 'Tags',
      footer: (props) => props.column.id,
      sortable: false,
      width: 70,
      cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
    }),
    // columnHelper.accessor('data.accessSettings.effectiveAccessLevel', {
    //   header: () => `Access`,
    //   footer: (props) => props.column.id,
    //   width: 130,
    //   sortable: false,
    //   cell: (cell) => cell.row.original.data?.accessSettings?.effectiveAccessLevel || AccessLevelType.Free,
    // }),
    // columnHelper.accessor('embargoDate', {
    //   header: () => `Embargo (${ouData.timezoneAbbreviation})`,
    //   footer: (props) => props.column.id,
    //   width: 130,
    //   sortable: false,
    //   cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
    // }),
    // columnHelper.accessor('expireDate', {
    //   header: `Expiry (${ouData.timezoneAbbreviation})`,
    //   footer: (props) => props.column.id,
    //   width: 130,
    //   sortable: false,
    //   cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
    // }),
    columnHelper.accessor('durationString', {
      header: 'Duration',
      footer: (props) => props.column.id,
      width: 75,
      sortable: false,
      cell: (cell) => <AssetDuration item={cell.row.original} />,
    }),
    columnHelper.accessor('actions', {
      header: 'Actions',
      footer: (props) => props.column.id,
      width: 75,
      sortable: false,
      cell: (cell) => (
        <FuelActionbar
          actions={[
            {
              title: 'Delete',
              className: 'action',
              type: 'delete',
              iconClass: 'icon material-icons',
              iconName: 'delete',
              isDisabled: false,
            },
          ]}
          item={cell.row.original}
          onActionButtonClick={onTableAction}
          context={cell}
        />
      ),
    }),
  ];

  const onTableAction = async (event, type, item, context: any) => {
    if (type === 'delete') {
      const myItems = _.cloneDeep(context.table.options.data); // cloneDeep needed here so react knows the object is different
      const foundIndex = _.findIndex(myItems, (x) => x.id === item.id);
      if (foundIndex > -1) {
        myItems.splice(foundIndex, 1);
        setItems(myItems);
        const ids = myItems.map((item) => item.id);
        setValue('clipIds', ids);
        setValue('clipGroupContentClips', myItems);
        const myPagination = _.cloneDeep(initialPagination);
        myPagination.totalItems = myItems.length;
        setPagination(myPagination);
        // if (onFormChanged) onFormChanged();
      }
    }
  };
  const onDrag = (newItems: any[], lastDroppedElement) => {
    let myAssets: Item[] = items;
    if (lastDroppedElement) {
      if (lastDroppedElement.source === 'ClipGroup') {
        alert('Clip Group assets cannot be used in a Clip Group.', 'warning');
        return;
      }
      if (lastDroppedElement.source === 'NestedChannel') {
        alert('NestedChannel assets cannot be used in a Clip Group.', 'warning');
        return;
      }
      if (_.find(myAssets, (i) => i.id === lastDroppedElement.id)) {
        alert('Cannot add a duplicate item');
        return;
      }
    }
    // filter out duplicates
    newItems = _.uniqBy(newItems, 'id');
    myAssets = enhanceFetchedAssets(newItems, permissions, flags, true, [], [], ouData);
    setValue('clipGroupContentClips', myAssets);
    const myPagination = _.cloneDeep(initialPagination);
    myPagination.totalItems = myAssets.length;
    setPagination(myPagination);
    const ids = newItems.map((item) => item.id);
    setValue('clipIds', ids);
    setValue('clipGroup.clipIds', ids);
    setItems(newItems);
    // if (onFormChanged) onFormChanged();
  };

  return (
    <div className="clipgroup-lod-content fullHeight" id="clipgroup-lod-content">
      <div className="page-column-wrapper fullHeight">
        <div className="page-left-column">
          <FuelAssetSearch
            excludeAssetFilterTypes={GetAssetFilterTypeOptions([
              AssetFilterType.ClipGroup,
              AssetFilterType.Transitions,
              AssetFilterType.Image,
              AssetFilterType.Series,
              AssetFilterType.Season,
              AssetFilterType.Extra,
              AssetFilterType.Trailer,
              AssetFilterType.LiveEvent,
              AssetFilterType.ExternalClip,
            ])}
            showStatus
          />
        </div>
        <div className="page-right-column fullHeight">
          <div className="clipgroup-content-grid-wrapper fullHeight">
            <FuelTable
              columnHelper={columnHelper}
              data={items || []}
              defaultColumns={columns}
              showRowSelection={false}
              paginationDetail={pagination}
              drop
              drag
              onDrag={onDrag}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentClipsTab;
